import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { UserCircle2 } from "lucide-react";

/**
 * Reception sign-out — combobox of known requesters + free-text.
 * Types-as-filter over an admin-managed list; if the typed name doesn't match
 * anything, the take endpoint auto-upserts it so next time the picker has it.
 */
const RequesterCombobox = ({ value, onChange, testid = "requester" }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api
      .get("/requesters")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const q = (value || "").trim().toLowerCase();
  const filtered = useMemo(
    () =>
      rows.filter((r) =>
        !q ? true : r.name.toLowerCase().includes(q),
      ),
    [rows, q],
  );

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <label className="label-eyebrow mb-1 flex items-center gap-1">
        <UserCircle2 size={12} strokeWidth={2.5} /> Requester (optional)
      </label>
      <input
        data-testid={`${testid}-input`}
        className="nb-input w-full"
        value={value || ""}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Name or clinician…"
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 max-h-40 overflow-y-auto rounded-lg border-2 border-black bg-white shadow-lg z-10"
          data-testid={`${testid}-list`}
        >
          {filtered.slice(0, 20).map((r) => (
            <button
              key={r.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(r.name);
                setOpen(false);
              }}
              data-testid={`${testid}-option-${r.name}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#F1E7FA] border-b border-[#E0E5EA] last:border-b-0"
            >
              {r.name}
            </button>
          ))}
        </div>
      )}
      {value && q && !filtered.some((r) => r.name.toLowerCase() === q) && (
        <div className="text-[10px] text-[#5B3A99] mt-1">
          &ldquo;{value}&rdquo; is new — we&apos;ll add it to the list on confirm.
        </div>
      )}
    </div>
  );
};

export default RequesterCombobox;
